/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import SelectProvider from './accountWorkflow/SelectProvider';
import RedirectToProvider from './accountWorkflow/RedirectToProvider';
import AccountData from './accountWorkflow/AccountData';
import RealEstateAccountData from './accountWorkflow/RealEstateAccountData';
import OverlayCard from '../../elements/OverlayCard';
import NavComponent2 from './NavComponent2';
import { Step1AccountName, Step2ProductType, Step3Contributions, Step4Payouts, Step5PayoutsCurrentPrognosis, Step6ConnectedAccount } from './accountWorkflow/pension';
import ObjectsOfValueAccountData from './accountWorkflow/ObjectsOfValueAccountData';
import MetalsAccountData from './accountWorkflow/MetalsAccountData';
import UnlistedSharesAccountData from './accountWorkflow/UnlistedSharesAccountData';
import CryptoAccountData from './accountWorkflow/CryptoAccountData';
import handleSubmitWorkflow from './accountWorkflow/handleSubmit';

const debugLevel = process.env.REACT_APP_MDEBUG || 0;

/**
 * This component manages the new account workflow. Workflow is defined in the workflows object. Each subsequent step updates workflowStepStatus.
 * When workflowStepStatus has only true values, the Submit button is displayed.
 *
 * @param {string} category - was set by Dashboard > DashboardCategory when the user clicked on "Add New" button
 * @param {object} addModeState - controls the display of this add account dialog { visible, category }
 * @param {func} setAddModeState - sets the addModeState
 * @param {object} addAccountState - the state of the add account workflow { category, userInput, workflowStepStatus, displayedStep (?) }
 *
 * If addAccountState is present on initialisation, it means the workflow is being restarted (e.g. after redirecting to openbanking provider via AddAccountCallbakReceiver and Dashboard)
 *
 * @returns React.Component
 */
export default function AddAccount2({ category, addModeState, setAddModeState, addAccountState }) {
  const myRef = useRef(null);
  useEffect(() => {
    myRef.current.scrollIntoView({ behavior: 'instant' });
  }, []);

  const dispatch = useDispatch();

  const { t } = useTranslation('addAccount');

  // const [userInput, setUserInput] = useState(addAccountState?.userInput || {}); // being assembled during the workflow from the inputs of different child components
  const form = useForm({ mode: 'onChange', shouldUnregister: false, defaultValues: addAccountState?.userInput || {} });
  const { control, getValues } = form;

  const [loading, setLoading] = useState(false); // true when the user has submitted the form and we are waiting for the backend to respond
  const [displayedStep, setDisplayedStep] = useState(addAccountState?.displayedStep || 0); // the index of the component that is currently displayed
  const [messageToShowBeforeNext, setMessageToShowBeforeNext] = useState(null); // if this is not null, show the message in a popup before moving to the next step
  const [signalNext, setSignalNext] = useState(false); // passes the event of user clicking NEXT in NAV COMPONENT to the current component

  // -----------------------------------------------------------
  // ESC handler
  // -----------------------------------------------------------

  useEffect(() => {
    // if user presses esc, go back to dashboard
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setAddModeState({ visible: false, category: null, addAccountState: undefined });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  //------------------------------------------------------------
  // DEFINE ALL WORKFLOWS
  //------------------------------------------------------------

  const workflows = {
    deposits: [SelectProvider, RedirectToProvider, AccountData],
    stocks: [SelectProvider, RedirectToProvider, AccountData],
    realEstate: [RealEstateAccountData],
    loans: [SelectProvider, RedirectToProvider, AccountData],
    pension: [Step1AccountName, Step2ProductType, Step3Contributions, Step4Payouts, Step5PayoutsCurrentPrognosis, Step6ConnectedAccount],
    objectsOfValue: [ObjectsOfValueAccountData],
    metals: [MetalsAccountData],
    unlistedShares: [UnlistedSharesAccountData],
    crypto: [CryptoAccountData],
  };
  const currentWorkflow = workflows[category]; // an array that holds the components constituting the workflow in the proper sequence

  //------------------------------------------------------------
  // SET UP THIS WORKFLOW'S STATE
  //------------------------------------------------------------

  // set "workflowStepStatus" to an array of the same amount of items as the workflow calculated in the "workflows" object
  const [workflowStepStatus, setWorkflowStepStatus] = useState(addAccountState?.workflowStepStatus || [...Array(currentWorkflow.length)]);

  // update the status of a single step in the workflow to 'status'; this function will be passed to the child components so that they may update this state
  function updateWorkflowStatus(step, status) {
    if (debugLevel > 2) console.info(`updateWorkflowStatus: step ${step} status ${status}`);

    const newStatus = [...workflowStepStatus];
    newStatus[step] = status;
    setWorkflowStepStatus(newStatus);
  }

  // workflow is ready (and the Submit button is displayed) when all of its steps are ready
  const formReady = workflowStepStatus.every((step) => step === true);
  if (debugLevel > 2) console.info('formReady', formReady, workflowStepStatus);

  //------------------------------------------------------------
  // PREPARE WORKFLOW STATUS INDICATOR
  //------------------------------------------------------------

  const steps = useMemo(
    () => currentWorkflow.map((item, index) => ({
      name: item.name,
      status: index === displayedStep ? 'current' : index < displayedStep ? 'complete' : 'upcoming',
    })),
    [currentWorkflow, displayedStep],
  );

  // ------------------------------------------------------------
  // ↓↓ main SUBMIT handler for the AddAccount workflows
  // ------------------------------------------------------------

  async function handleSubmit(e) {
    const formValues = getValues();
    await handleSubmitWorkflow({
      category,
      workflowStepStatus,
      formValues,
      dispatch,
      setLoading,
      setAddModeState,
      t,
    });
  }

  function handleClose(e) {
    e.preventDefault();
    setAddModeState({ ...addModeState, visible: false });
  }

  const Component = currentWorkflow[displayedStep];

  return (
    <OverlayCard closeCallback={handleClose} fixedHeight noPadding>
      <main className="flex flex-col h-full" ref={myRef}>
        <div className="flex-grow px-2 sm:px-8">
          <div className="px-4 py-5 sm:px-8 sm:py-8">
            <Component
              category={category}
              index={displayedStep}
              setDisplayedStep={setDisplayedStep}
              workflowStepStatus={workflowStepStatus}
              updateWorkflowStatus={updateWorkflowStatus}
              setMessageToShowBeforeNext={setMessageToShowBeforeNext}
              form={form}
              signalNext={signalNext}
              setSignalNext={setSignalNext}
            />
          </div>
        </div>
        <NavComponent2
          setAddModeState={setAddModeState}
          displayedStep={displayedStep}
          setDisplayedStep={setDisplayedStep}
          workflowStepStatus={workflowStepStatus}
          form={form}
          steps={steps}
          loading={loading}
          messageToShowBeforeNext={messageToShowBeforeNext}
          setMessageToShowBeforeNext={setMessageToShowBeforeNext}
          handleSubmit={handleSubmit}
          setSignalNext={setSignalNext}
        />
      </main>
    </OverlayCard>
  );
}
AddAccount2.propTypes = {
  category: PropTypes.string.isRequired,
  addModeState: PropTypes.objectOf(PropTypes.any).isRequired,
  setAddModeState: PropTypes.func.isRequired,
  addAccountState: PropTypes.objectOf(PropTypes.any),
};
AddAccount2.defaultProps = {
  addAccountState: null,
};
